import React, { useReducer, useEffect, useState } from "react";
import { Routes, Route, useParams, useNavigate, Outlet } from "react-router-dom";
import ExchangeSubscriptionsList from "./ExchangeSubscriptionList";
import ExchangeReason from "./ExchangeReason";
import CurrentVehicle from "./CurrentVehicle";
import NewVehicle from "./NewVehicle";
import { currentVehicleReducer, currentVehicleInitialState } from "./currentVehicleDetailsReducer";
import { exchangeReasonInitialState, exchangeReasonReducer } from "./exchangeReasonReducer";
import { defaultNewVehicleState, newVehicleReducer } from "./newVehicleReducer";
import ExchangeReview from "./ExchangeReview";
import { selectedSubscriptionInitialState, selectedSubscriptionReducer } from "./selectedSubscriptionReducer";
import AdditionalCharges from "./AdditionalCharges";
import { additionalChargesInitialState, additionalChargesReducer } from "./additionalChargesReducer";
import Warning from "@components/modal/info/Warning";

// Enum for vehicle exchange journey paths
export enum VehicleExchangePath {
  Root = "",
  ExchangeReason = "exchange-reason",
  CurrentVehicle = "current-vehicle",
  AdditionalCharges = "additional-charges",
  NewVehicle = "new-vehicle",
  Review = "review"
}

export default function ExchangeParent() {
  const { custId, country } = useParams();
  const navigate = useNavigate();

  const [selectedSubscriptionState, selectedSubscriptionDispatch] = useReducer(selectedSubscriptionReducer, selectedSubscriptionInitialState);
  const [currentVehicleState, currentVehicleDispatch] = useReducer(currentVehicleReducer, currentVehicleInitialState);
  const [exchangeReasonState, exchangeReasonDispatch] = useReducer(exchangeReasonReducer, exchangeReasonInitialState);
  const [additionalChargesState, additionalChargesDispatch] = useReducer(additionalChargesReducer, additionalChargesInitialState);
  const [newVehicleState, newVehicleDispatch] = useReducer(newVehicleReducer, defaultNewVehicleState)
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("Unknown error");

  function buildVehicleExchangePath(targetComponent: VehicleExchangePath) {
    return `/vehicle-exchange/${country}/${custId}/${targetComponent}`;
  }

  // Validate all required states up to and including the current route
  function validateJourneyState(window_path: string): string | null {
    // Always check subscription for all steps except select-sub
    if (
      window_path.endsWith(`/${VehicleExchangePath.ExchangeReason}`) ||
      window_path.endsWith(`/${VehicleExchangePath.CurrentVehicle}`) ||
      window_path.endsWith(`/${VehicleExchangePath.AdditionalCharges}`) ||
      window_path.endsWith(`/${VehicleExchangePath.NewVehicle}`) ||
      window_path.endsWith(`/${VehicleExchangePath.Review}`)
    ) {
      if (!selectedSubscriptionState.selectedSubsId) return "No selected subscription found.";
    }
    // Check exchange reason for all steps after exchange-reason
    if (
      window_path.endsWith(`/${VehicleExchangePath.CurrentVehicle}`) ||
      window_path.endsWith(`/${VehicleExchangePath.AdditionalCharges}`) ||
      window_path.endsWith(`/${VehicleExchangePath.NewVehicle}`) ||
      window_path.endsWith(`/${VehicleExchangePath.Review}`)
    ) {
      if (!exchangeReasonState.exchangeReason || exchangeReasonState.exchangeReason.trim().length === 0)
        return "No exchange reason found.";
    }
    // Check current vehicle for all steps after current-vehicle
    if (
      window_path.endsWith(`/${VehicleExchangePath.AdditionalCharges}`) ||
      window_path.endsWith(`/${VehicleExchangePath.NewVehicle}`) ||
      window_path.endsWith(`/${VehicleExchangePath.Review}`)
    ) {
      if (!currentVehicleState.vehicle || !currentVehicleState.vehicle.id || currentVehicleState.vehicle.id.trim().length === 0)
        return "No current vehicle found.";
    }
    // Check new vehicle for review page
    if (window_path.endsWith(`/${VehicleExchangePath.Review}`)) {
      if (!newVehicleState.selectedVehicle || !newVehicleState.selectedVehicle.id || newVehicleState.selectedVehicle.id.trim().length === 0)
        return "No new vehicle found.";
    }
    return null;
  }

  // Validate state is set, when the path changes
  useEffect(() => {
    // Use pathname for route matching
    const error = validateJourneyState(window.location.hash);
    if (error) {
      setErrorMessage(error);
      setShowErrorModal(true);
    } else {
      setShowErrorModal(false);
      setErrorMessage("");
    }
  }, [window.location.pathname]);

  function handleStateErrorModalClose() {
    // If state error, sends the user back to the start of the journey
    setShowErrorModal(false);
    navigate(buildVehicleExchangePath(VehicleExchangePath.Root)); // Go (back) to start
  }

  return (
    <>
      {showErrorModal && (
        <Warning
          message={errorMessage}
          additionalInfo={"The Vehicle Exchange Journey has encountered an error. Missing state from a previous step. You must restart the journey."}
          onClickClose={handleStateErrorModalClose}
        />
      )}
      <Routes>
        <Route
          path={VehicleExchangePath.Root}
          element={
            <ExchangeSubscriptionsList
              subscriptionStateDispatch={selectedSubscriptionDispatch}
              subscriptionState={selectedSubscriptionState}
              navigateTarget={buildVehicleExchangePath(VehicleExchangePath.ExchangeReason)}
            />
          }
        />
        <Route
          path={VehicleExchangePath.ExchangeReason}
          element={
            <ExchangeReason
              navigateTarget={buildVehicleExchangePath(VehicleExchangePath.CurrentVehicle)}
              exchangeReasonState={exchangeReasonState}
              exchangeReasonDispatch={exchangeReasonDispatch}
              selectedSubscriptionState={selectedSubscriptionState}
            />
          }
        />
        <Route
          path={VehicleExchangePath.CurrentVehicle}
          element={
            <CurrentVehicle
              navigateTarget={buildVehicleExchangePath(VehicleExchangePath.AdditionalCharges)}
              selectedSubscriptionState={selectedSubscriptionState}
              currentVehicleState={currentVehicleState}
              currentVehicleDispatcher={currentVehicleDispatch}
            />
          }
        />
        <Route
          path={VehicleExchangePath.AdditionalCharges}
          element={
            <AdditionalCharges
              navigateTarget={buildVehicleExchangePath(VehicleExchangePath.NewVehicle)}
              currentVehicleState={currentVehicleState}
              additionalChargesState={additionalChargesState}
              additionalChargesDispatch={additionalChargesDispatch}
            />
          }
        />
        <Route
          path={VehicleExchangePath.NewVehicle}
          element={
            <NewVehicle
              navigateTarget={buildVehicleExchangePath(VehicleExchangePath.Review)}
              selectedSubscriptionState={selectedSubscriptionState}
              currentVehicleState={currentVehicleState}
              newVehicleState={newVehicleState}
              newVehicleDispatcher={newVehicleDispatch}
              exchangeReasonState={exchangeReasonState}
            />
          }
        />
        <Route
          path={VehicleExchangePath.Review}
          element={
            <ExchangeReview
              selectedSubscriptionState={selectedSubscriptionState}
              currentVehicleState={currentVehicleState}
              newVehicleState={newVehicleState}
              exchangeReasonState={exchangeReasonState}
              additionalChargesState={additionalChargesState}
            />
          }
        />
      </Routes>
      <Outlet />
    </>
  );
}
